import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowLeft, Star, TrendUp, Clock, Coins, Package, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";
import { Card, Loading } from "@/components/riq";

export default function ItemDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cur = user?.currency || "ZAR";
  const [data, setData] = useState(null);
  const [tracked, setTracked] = useState(false);

  useEffect(() => {
    api.get(`/items/history?name=${encodeURIComponent(name)}`).then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/items/tracked").then((r) => setTracked(r.data.some((t) => t.canonical_name === decodeURIComponent(name))));
  }, [name]);

  const toggleTrack = async () => {
    try {
      if (tracked) await api.delete(`/items/track?name=${encodeURIComponent(decodeURIComponent(name))}`);
      else await api.post("/items/track", { canonical_name: decodeURIComponent(name) });
      setTracked(!tracked);
      toast.success(tracked ? "Removed from tracked" : "Now tracking this item");
    } catch { toast.error("Could not update"); }
  };

  if (data === null) return <Loading />;
  if (data === false) return <div className="text-muted-foreground">Item not found.</div>;

  const priceData = (data.price_trend || []).map((p) => ({ date: p.date, price: p.price }));

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-neutral-500 font-medium"><ArrowLeft size={18} /> Back</button>

      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{data.category}{data.size ? ` · ${data.size}` : ""}</div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary mt-1">{data.canonical_name}</h1>
        </div>
        <button data-testid="item-track-btn" onClick={toggleTrack} className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${tracked ? "bg-riq-gold/20 text-riq-primary" : "border border-riq-border"}`}>
          <Star size={18} weight={tracked ? "fill" : "regular"} className={tracked ? "text-riq-gold" : ""} /> {tracked ? "Tracking" : "Track item"}
        </button>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Package} label="Purchases" value={data.purchases} />
        <Stat icon={Coins} label="Total spent" value={money(data.total_spend, cur)} />
        <Stat icon={Coins} label="Avg. price" value={data.average_price != null ? money(data.average_price, cur) : "—"} />
        <Stat icon={Clock} label="Last bought" value={shortDate(data.last_purchase)} />
      </div>

      {/* Pattern */}
      <Card>
        <h3 className="font-head font-semibold text-riq-primary mb-3">Purchase pattern</h3>
        {data.enough_data ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-riq-sage/40 p-4 text-sm text-riq-primary flex items-start gap-3">
              <TrendUp size={20} weight="duotone" className="shrink-0 mt-0.5" />
              <span>Based on your previous purchases, you typically buy this around <b>every {Math.round(data.median_interval_days)}–{Math.ceil(data.average_interval_days)} days</b>.</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <MiniStat label="Avg. interval" value={`${data.average_interval_days} days`} />
              <MiniStat label="Median interval" value={`${data.median_interval_days} days`} />
              <MiniStat label="Avg. quantity" value={data.average_quantity} />
              <MiniStat label="Min price" value={money(data.min_price, cur)} />
              <MiniStat label="Max price" value={money(data.max_price, cur)} />
              {data.price_change_pct != null && <MiniStat label="Price change" value={`${data.price_change_pct > 0 ? "+" : ""}${data.price_change_pct}%`} accent={data.price_change_pct > 0} />}
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
            <WarningCircle size={20} weight="fill" className="shrink-0 mt-0.5" />
            {data.message || "Not enough purchase history yet. Add a few more receipts and we'll calculate your purchase pattern."}
          </div>
        )}
      </Card>

      {/* Price trend */}
      {priceData.length >= 2 && (
        <Card>
          <h3 className="font-head font-semibold text-riq-primary mb-4">Price trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={priceData} margin={{ left: -10, right: 10, top: 5 }}>
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} minTickGap={30} />
              <YAxis tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} width={45} tickFormatter={(v) => money(v, cur)} />
              <Tooltip formatter={(v) => money(v, cur)} labelFormatter={shortDate} contentStyle={{ borderRadius: 12, border: "1px solid #E9EAF2", fontSize: 13 }} />
              <Line type="monotone" dataKey="price" stroke="#6E3FFC" strokeWidth={2.5} dot={{ r: 3, fill: "#6E3FFC" }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Merchant comparison */}
      {data.merchant_comparison?.length >= 2 && (
        <Card>
          <h3 className="font-head font-semibold text-riq-primary mb-4">Price by merchant</h3>
          <div className="space-y-2">
            {data.merchant_comparison.map((m, i) => (
              <div key={m.merchant} className="flex items-center justify-between rounded-xl bg-secondary/40 px-4 py-3">
                <span className="text-sm font-medium">{m.merchant}{i === 0 && <span className="ml-2 text-xs text-riq-primary2 font-semibold">cheapest</span>}</span>
                <span className="font-head font-semibold">{money(m.average_price, cur)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Timeline */}
      {data.purchase_timeline?.length > 0 && (
        <Card>
          <h3 className="font-head font-semibold text-riq-primary mb-4">Purchase timeline</h3>
          <div className="flex flex-wrap gap-2">
            {data.purchase_timeline.map((d, i) => (
              <span key={i} className="rounded-full bg-riq-primary/8 border border-riq-border px-3 py-1.5 text-xs">{shortDate(d)}</span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <Icon size={18} weight="duotone" className="text-riq-primary mb-2" />
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-lg font-bold text-riq-primary mt-0.5">{value}</div>
    </Card>
  );
}
function MiniStat({ label, value, accent }) {
  return <div className="rounded-xl bg-secondary/40 p-3"><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-head font-bold mt-0.5" style={{ color: accent ? "#FF6B6B" : "#6E3FFC" }}>{value}</div></div>;
}
